import { useCallback, useEffect, useRef, useState } from "react";
import { MutationSaveStatus } from "./MutationSaveStatus";

const SETTLED_DISPLAY_DURATION_MS = 2000;

interface UseMutationSaveStatusReturn {
  status: MutationSaveStatus;
  markSaving: () => void;
  markSettled: (hasError: boolean) => void;
}

/**
 * Tracks the visual save status (idle/saving/saved/error) of a GraphQL mutation
 * fired outside of a form submit, so fields without a save button can still show
 * the user whether their change was persisted.
 */
export function useMutationSaveStatus(): UseMutationSaveStatusReturn {
  const [status, setStatus] = useState<MutationSaveStatus>("idle");
  const settledTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      if (settledTimeoutRef.current) {
        clearTimeout(settledTimeoutRef.current);
      }
    };
  }, []);

  const markSaving = useCallback(() => {
    if (settledTimeoutRef.current) {
      clearTimeout(settledTimeoutRef.current);
    }
    setStatus("saving");
  }, []);

  const markSettled = useCallback((hasError: boolean) => {
    if (settledTimeoutRef.current !== undefined) {
      clearTimeout(settledTimeoutRef.current);
    }

    setStatus(hasError ? "error" : "saved");
    settledTimeoutRef.current = setTimeout(
      () => setStatus("idle"),
      SETTLED_DISPLAY_DURATION_MS,
    );
  }, []);

  return { status, markSaving, markSettled };
}
